/**
 * AssistantHome.jsx
 * src/pages/assistant/AssistantHome.jsx
 *
 * Dashboard home page for the Assistant role.
 * Dependencies: npm install recharts bootstrap
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { apiFetch } from "../../components/helpers";

// Constants removed - now using dynamic data from API

const STATUS_STYLE = {
  "Completed":   { bg: "#E1F5EE", color: "#085041", badge: "text-bg-success"  },
  "In Progress": { bg: "#FAEEDA", color: "#633806", badge: "text-bg-warning"  },
  "Waiting":     { bg: "#E6F1FB", color: "#042C53", badge: "text-bg-info"     },
  "Pending":     { bg: "#F0F4F8", color: "#6B7A8D", badge: "text-bg-secondary"},
  "Cancelled":   { bg: "#FCEBEB", color: "#501313", badge: "text-bg-danger"   },
};

function useAnimatedCounter(target, duration = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let frame, start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setValue(Math.floor((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
}

function Avatar({ name, size = 32, bg, color }) {
  const safeName = name || "Unknown";
  const ini = safeName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const palettes = [["#E1F5EE","#085041"],["#FAEEDA","#633806"],["#E6F1FB","#042C53"],["#EEEDFE","#3C3489"],["#FCEBEB","#501313"]];
  const [pbg, pc] = palettes[(safeName.charCodeAt(0) || 0) % palettes.length];
  return (
    <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
      style={{ width: size, height: size, background: bg || pbg, color: color || pc, fontSize: size * 0.35, fontWeight: 700 }}>
      {ini}
    </div>
  );
}

function StatCard({ icon, label, value, sub, subColor, accent }) {
  const count = useAnimatedCounter(typeof value === "number" ? value : 0);
  const displayValue = typeof value === "number" ? count : value;
  return (
    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
      <div className="card-body p-3" style={{ borderLeft: `3px solid ${accent}`, borderRadius: 14 }}>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <p className="text-muted mb-0 fw-bold" style={{ fontSize: 10, letterSpacing: ".7px", textTransform: "uppercase" }}>{label}</p>
          <span style={{ fontSize: 22, opacity: 0.1 }}>{icon}</span>
        </div>
        <h3 className="mb-1 fw-bold" style={{ color: "#0A1628", fontSize: 26, lineHeight: 1 }}>{displayValue}</h3>
        {sub && <p className="mb-0" style={{ fontSize: 11, fontWeight: 600, color: subColor || "#6B7A8D" }}>{sub}</p>}
      </div>
    </div>
  );
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-3 px-3 py-2" style={{ background: "#0A1628", color: "#fff", fontSize: 12 }}>
      <p className="mb-1" style={{ opacity: .6, fontSize: 11 }}>{label}</p>
      <p className="mb-0 fw-bold">{payload[0].value} patients</p>
    </div>
  );
};

export default function AssistantHome() {
  const navigate = useNavigate();
  const [schedule,    setSchedule]    = useState([]);
  const [doctorsList, setDoctorsList] = useState([]);
  const [hourlyData,  setHourlyData]  = useState([]);
  const [followups,   setFollowups]   = useState([]);
  const [stats,       setStats]       = useState({ total: 0, checkedIn: 0, inProgress: 0, completed: 0 });
  const [loading,     setLoading]     = useState(true);
  const [searchQ,     setSearchQ]     = useState("");
  const [checkinId,   setCheckinId]   = useState(null);

  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const assistantId = localStorage.getItem("admin_id");
        const json = await apiFetch(`${process.env.REACT_APP_ASSISTANT_DASHBOARD}`, {
          method: "POST",
          body: { assistant_id: assistantId }
        });

        if (json.status) {
          setSchedule(json.schedule || []);
          setDoctorsList(json.doctors || []);
          setHourlyData(json.hourly_flow || []);
          setFollowups(json.followups || []);
          setStats({
            total: json.today_appointments || 0,
            checkedIn: (json.schedule || []).filter(s => s.checkedIn == 1).length,
            inProgress: (json.schedule || []).filter(s => s.status === "In Progress" || s.status === "Waiting").length,
            completed: (json.schedule || []).filter(s => s.status === "Completed").length,
          });
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  const authUser = (() => { try { return JSON.parse(localStorage.getItem("adminuser") || localStorage.getItem("auth_user") || "{}"); } catch { return {}; } })();

  const completed   = stats.completed || 0;
  const inProgress  = stats.inProgress || 0;
  const checkedIn   = stats.checkedIn || 0;
  const pending     = (stats.total || 0) - checkedIn;

  const handleCheckin = async (idx, id) => {
    try {
      const json = await apiFetch(`${process.env.REACT_APP_UPDATE_APPOINTMENT_STATUS}`, { 
        method: "POST", 
        body: { appointment_id: id, status: "Waiting" } 
      });
      if (json.status) {
        setSchedule(prev => prev.map((s, i) =>
          i === idx ? { ...s, checkedIn: 1, status: s.status === "Pending" ? "Waiting" : s.status } : s
        ));
        setCheckinId(idx);
        setTimeout(() => setCheckinId(null), 2000);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = schedule.filter(s =>
    s.patient.toLowerCase().includes(searchQ.toLowerCase()) ||
    s.doctor.toLowerCase().includes(searchQ.toLowerCase()) ||
    s.treatment.toLowerCase().includes(searchQ.toLowerCase())
  );

  return (
    <div className="p-3 p-lg-4" style={{ background: "#EEF2F7", minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}>

      {/* ── HEADER ── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center justify-content-center rounded-3"
            style={{ width: 44, height: 44, background: "#FAECE7", border: "1px solid #F5C4B3" }}>
            <span style={{ fontSize: 22 }}>🤝</span>
          </div>
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "#0A1628" }}>
              Good {new Date().getHours() < 12 ? "Morning" : new Date().getHours() < 17 ? "Afternoon" : "Evening"}, {authUser.name?.split(" ")[0] || "Assistant"} 👋
            </h5>
            <small className="text-muted">{today}</small>
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <span className="badge px-3 py-2" style={{ background: "#FAECE7", color: "#D85A30", fontSize: 12, fontWeight: 700, borderRadius: 20 }}>
            🤝 Assistant Panel
          </span>
          <span className="badge px-3 py-2" style={{ background: "#E1F5EE", color: "#085041", fontSize: 12, fontWeight: 700, borderRadius: 20 }}>
            📅 {schedule.length} Appointments Today
          </span>
        </div>
      </div>

      {/* ── STAT CARDS ── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-lg-3">
          <StatCard icon="📅" label="Today's Total"  value={schedule.length} sub="Appointments scheduled" accent="#D85A30" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="✅" label="Checked In"     value={checkedIn}       sub={`${pending} still pending`} subColor="#D85A30" accent="#1D9E75" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="🔄" label="In Progress"    value={inProgress}      sub="Being attended"           accent="#378ADD" />
        </div>
        <div className="col-6 col-lg-3">
          <StatCard icon="✔️" label="Completed"      value={completed}       sub="Done for today"           subColor="#1D9E75" accent="#7F77DD" />
        </div>
      </div>

      {/* ── HOURLY CHART + DOCTOR STATUS ── */}
      <div className="row g-3 mb-4">

        <div className="col-12 col-xl-7">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body p-3 p-lg-4">
              <p className="text-muted fw-bold mb-3" style={{ fontSize: 10, letterSpacing: ".8px", textTransform: "uppercase" }}>
                Hourly Patient Flow
              </p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={hourlyData} barCategoryGap="38%">
                  <CartesianGrid vertical={false} stroke="#F0F4F8" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#6B7A8D" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B7A8D" }} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(216,90,48,0.05)" }} />
                  <Bar dataKey="patients" fill="#D85A30" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-5">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body p-3">
              <p className="text-muted fw-bold mb-3" style={{ fontSize: 10, letterSpacing: ".8px", textTransform: "uppercase" }}>
                Doctor Availability
              </p>
              <div className="d-flex flex-column gap-2">
                {doctorsList.length === 0 ? <p className="text-muted small">No doctors data</p> : doctorsList.map((doc) => {
                  const ini = doc.name.replace("Dr. ", "").split(" ").map(n => n[0]).join("").slice(0, 2);
                  const statusColors = {
                    "Active": { bg: "#E1F5EE", text: "#085041" },
                    "With Patient": { bg: "#FAEEDA", text: "#633806" },
                    "On Leave": { bg: "#FCEBEB", text: "#501313" },
                    "On Break": { bg: "#E6F1FB", text: "#042C53" }
                  };
                  const theme = statusColors[doc.status] || statusColors["Active"];
                  return (
                    <div key={doc.name} className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2"
                      style={{ background: theme.bg, border: `1px solid ${theme.bg}` }}>
                      <div className="d-flex align-items-center gap-2">
                        <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          style={{ width: 32, height: 32, background: doc.textColor, color: "#fff", fontSize: 11, fontWeight: 700 }}>
                          {ini}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#0A1628" }}>{doc.name}</div>
                          <div style={{ fontSize: 11, color: "#6B7A8D" }}>{doc.spec}</div>
                        </div>
                      </div>
                      <span className="badge" style={{ fontSize: 10, fontWeight: 700, background: theme.text, color: "#fff" }}>
                        {doc.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── TODAY'S SCHEDULE TABLE ── */}
      <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 16 }}>
        <div className="card-body p-0">
          <div className="px-4 pt-3 pb-2 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <p className="text-muted fw-bold mb-0" style={{ fontSize: 10, letterSpacing: ".8px", textTransform: "uppercase" }}>
              Today's Schedule
            </p>
            <input
              className="form-control form-control-sm"
              placeholder="Search patient / doctor..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              style={{ width: 220, fontSize: 12 }}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead style={{ background: "#FAFBFC" }}>
                <tr>
                  {["Time","Patient","Doctor","Treatment","Checked In","Status","Action"].map(h => (
                    <th key={h} className="text-muted fw-bold border-0 px-4 py-3"
                      style={{ fontSize: 10, letterSpacing: ".6px", textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((appt, i) => {
                  const ss = STATUS_STYLE[appt.status] || STATUS_STYLE.Pending;
                  return (
                    <tr key={i}>
                      <td className="px-4">
                        <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#0A1628" }}>
                          {appt.time}
                        </span>
                      </td>
                      <td className="px-4">
                        <div className="d-flex align-items-center gap-2">
                          <Avatar name={appt.patient} size={28} />
                          <span className="fw-semibold" style={{ fontSize: 13 }}>{appt.patient}</span>
                        </div>
                      </td>
                      <td className="px-4 fw-semibold" style={{ fontSize: 13, color: "#D85A30" }}>{appt.doctor}</td>
                      <td className="px-4 text-muted" style={{ fontSize: 13 }}>{appt.treatment}</td>
                      <td className="px-4">
                        {appt.checkedIn
                          ? <span className="badge text-bg-success" style={{ fontSize: 10 }}>✓ Checked In</span>
                          : <span className="badge text-bg-secondary" style={{ fontSize: 10 }}>Not Yet</span>}
                      </td>
                      <td className="px-4">
                        <span className={`badge ${ss.badge}`} style={{ fontSize: 11 }}>{appt.status}</span>
                      </td>
                      <td className="px-4">
                        {!appt.checkedIn ? (
                          <button
                            className="btn btn-sm fw-bold text-white"
                            style={{ background: "#D85A30", fontSize: 11, borderRadius: 8 }}
                            onClick={() => handleCheckin(schedule.indexOf(appt), appt.id)}
                          >
                            Check In
                          </button>
                        ) : checkinId === schedule.indexOf(appt) ? (
                          <span style={{ fontSize: 11, color: "#1D9E75", fontWeight: 700 }}>✓ Done!</span>
                        ) : (
                          <span style={{ fontSize: 11, color: "#6B7A8D" }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── BOTTOM ROW: Follow-ups + Quick Actions ── */}
      <div className="row g-3">

        {/* Follow-ups */}
        <div className="col-12 col-lg-7">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body p-3 p-lg-4">
              <p className="text-muted fw-bold mb-3" style={{ fontSize: 10, letterSpacing: ".8px", textTransform: "uppercase" }}>
                Upcoming Follow-ups
              </p>
              <div className="d-flex flex-column gap-2">
                {followups.length === 0 ? <p className="text-muted small">No upcoming follow-ups</p> : followups.map((f, i) => (
                  <div key={i} className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2"
                    style={{ background: f.urgent ? "#FAECE7" : "#F8FAFC", border: `1px solid ${f.urgent ? "#F5C4B3" : "#E4EAF0"}` }}>
                    <div className="d-flex align-items-center gap-2">
                      <Avatar name={f.patient} size={30} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#0A1628" }}>{f.patient}</div>
                        <div style={{ fontSize: 11, color: "#6B7A8D" }}>{f.treatment}</div>
                      </div>
                    </div>
                    <div className="text-end">
                      <div style={{ fontSize: 12, fontWeight: 700, color: f.urgent ? "#D85A30" : "#1D9E75" }}>{f.date}</div>
                      {f.urgent && (
                        <span className="badge text-bg-danger" style={{ fontSize: 9 }}>Urgent</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="col-12 col-lg-5">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body p-3 p-lg-4">
              <p className="text-muted fw-bold mb-3" style={{ fontSize: 10, letterSpacing: ".8px", textTransform: "uppercase" }}>
                Quick Actions
              </p>
              <div className="row g-2">
                {[
                  { icon: "➕", label: "New Appointment",  color: "#D85A30", bg: "#FAECE7", path: "/assistant/create-appointment"  },
                  { icon: "✅", label: "Patient Check-in", color: "#1D9E75", bg: "#E1F5EE", path: "/assistant/checkin" },
                  { icon: "💬", label: "Send Reminder",    color: "#378ADD", bg: "#E6F1FB", path: "/assistant/reminders" },
                  { icon: "📦", label: "Check Inventory",  color: "#7F77DD", bg: "#EEEDFE", path: "/assistant/inventory" },
                  { icon: "💰", label: "Generate Bill",    color: "#BA7517", bg: "#FAEEDA", path: "/assistant/billing" },
                  { icon: "📋", label: "View Reports",     color: "#6B7A8D", bg: "#F0F4F8", path: "/assistant/reports" },
                ].map((action) => (
                  <div key={action.label} className="col-6">
                    <button
                      className="btn w-100 d-flex align-items-center gap-2 py-2 px-3 rounded-3"
                      onClick={() => navigate(action.path)}
                      style={{
                        background:  action.bg,
                        border:      `1px solid ${action.color}22`,
                        color:       action.color,
                        fontSize:    12, fontWeight: 700,
                        transition: "all .15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 4px 12px ${action.color}30`; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <span style={{ fontSize: 16 }}>{action.icon}</span>
                      {action.label}
                    </button>
                  </div>
                ))}
              </div>

              {/* Summary bar */}
              <div className="mt-3 p-3 rounded-3" style={{ background: "#F8FAFC", border: "1px solid #E4EAF0" }}>
                <p className="mb-2 fw-bold text-muted" style={{ fontSize: 10, letterSpacing: ".6px", textTransform: "uppercase" }}>Today's Progress</p>
                <div className="d-flex justify-content-between mb-1">
                  <span style={{ fontSize: 11, color: "#6B7A8D" }}>Appointments Done</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#0A1628" }}>{completed} / {schedule.length}</span>
                </div>
                <div className="progress" style={{ height: 8, borderRadius: 8 }}>
                  <div
                    className="progress-bar"
                    style={{ width: `${Math.round((completed / schedule.length) * 100)}%`, background: "#D85A30", borderRadius: 8, transition: "width 1s ease" }}
                  />
                </div>
                <p className="mt-1 mb-0 text-muted" style={{ fontSize: 10 }}>
                  {Math.round((completed / schedule.length) * 100)}% complete
                </p>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );
}